import type { PublicationStatus } from '@diora/contracts';
import { Archive, ArchiveRestore, EyeOff, Send } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionMenu, Alert, Badge, Button, Modal, type ActionMenuItem } from './ui';
import type { AdminTranslationStatus } from '@diora/contracts';

/** Publication state, with a dot so it does not rely on hue alone. */
export function StatusBadge({ status }: { status: PublicationStatus }) {
  const { t } = useTranslation();
  const tone = status === 'PUBLISHED' ? 'success' : status === 'ARCHIVED' ? 'neutral' : 'warning';
  return (
    <Badge tone={tone} dot>
      {t(`status.${status}`)}
    </Badge>
  );
}

export function AvailabilityBadge({ status }: { status: 'AVAILABLE' | 'UNAVAILABLE' }) {
  const { t } = useTranslation();
  if (status === 'AVAILABLE') return null;
  return (
    <Badge tone="warning" dot>
      {t('status.UNAVAILABLE')}
    </Badge>
  );
}

/**
 * What this record is still missing.
 *
 * Renders nothing when the record is complete — a badge that is always there
 * stops being read.
 */
export function TranslationBadge({ translation }: { translation: AdminTranslationStatus }) {
  const { t } = useTranslation();
  if (translation.publishable) return null;

  const missing: string[] = [];
  if (translation.missingAr.length > 0) missing.push(t('bilingual.arabic'));
  if (translation.missingEn.length > 0) missing.push(t('bilingual.english'));

  if (missing.length === 0) {
    return <Badge tone="warning">{t('publish.blockedTitle')}</Badge>;
  }

  return (
    <Badge tone="warning" dot>
      {t('bilingual.missing')} · {missing.join(' · ')}
    </Badge>
  );
}

/** The blockers, spelled out, for a record that cannot go live yet. */
export function PublishBlockers({ translation }: { translation: AdminTranslationStatus }) {
  const { t } = useTranslation();
  // Defaulted rather than assumed: this renders whatever the API sent, and a
  // shape that varies by resource should degrade to "nothing to show" instead
  // of taking the page down.
  const blockers = translation.blockers ?? [];
  const reasons = translation.blockerReasons ?? {};
  if (translation.publishable || blockers.length === 0) return null;

  return (
    <Alert tone="warning">
      <p className="font-semibold">{t('publish.blockedTitle')}</p>
      <p>{t('publish.blockedIntro')}</p>
      <ul className="mt-2 list-disc" style={{ paddingInlineStart: 'var(--space-5)' }}>
        {blockers.map((blocker) => (
          <li key={blocker}>{blockerSentence(t, blocker, reasons[blocker] ?? [])}</li>
        ))}
      </ul>
    </Alert>
  );
}

/**
 * Advisories, for a record that publishes fine but could be better.
 *
 * Separate from `PublishBlockers` on purpose, and toned `info` rather than
 * `warning`: this is the alert a person meets *after* the thing that used to
 * refuse them, and one that looks like a refusal gets treated as one. Today it
 * carries a single case — a photo below the recommended resolution, which is
 * accepted, upscaled, and worth replacing when there is a better one.
 */
export function PublishWarnings({ translation }: { translation: AdminTranslationStatus }) {
  const { t } = useTranslation();
  // Defaulted, like the blockers above: an older API that does not send this
  // field should render nothing, not crash the form.
  const warnings = translation.warnings ?? {};
  const fields = Object.keys(warnings);
  if (fields.length === 0) return null;

  return (
    <Alert tone="info">
      <p className="font-semibold">{t('publish.noteTitle')}</p>
      <p>{t('publish.noteIntro')}</p>
      <ul className="mt-2 list-disc" style={{ paddingInlineStart: 'var(--space-5)' }}>
        {fields.map((field) => (
          <li key={field}>{blockerSentence(t, field, warnings[field] ?? [])}</li>
        ))}
      </ul>
    </Alert>
  );
}

/**
 * One blocker, as a sentence: the field, then why it fails.
 *
 * The intro above says "this record is missing", which is true of a translation
 * gap and false of most other blockers — `categoryId` means the chosen category
 * is not published, `longitude` can be out of range rather than absent, an
 * `endsAt` can simply precede its start. So the reason is carried here rather
 * than implied by the heading.
 */
function blockerSentence(
  t: ReturnType<typeof useTranslation>['t'],
  field: string,
  codes: string[],
): string {
  const label = fieldLabel(t, field);
  // A translation gap is what the field name already says; repeating "requires
  // a translation" after "Name (Arabic)" is noise.
  const code = codes.find((value) => value !== 'TRANSLATION_REQUIRED' && value !== 'REQUIRED_FOR_PUBLICATION');
  if (!code) return label;

  const reason = t(`publishReason.${code}`, { defaultValue: '' });
  return reason ? `${label} — ${reason}` : label;
}

/**
 * Blockers come back as field names — `nameAr`, `basePrice`, `imageId`. Turn
 * them into something a person reads, keeping the language suffix visible
 * because which language is missing is the whole point.
 *
 * The fallback is a generic translated phrase, never `field` itself. Printing
 * the raw name put a bare Latin `categoryId` in the middle of an Arabic alert —
 * and it was reached because the map guessed at a name (`categoryPublished`)
 * that the API never sends. A fallback that renders an internal identifier is
 * the same defect as `messageForError` rendering a library's English.
 */
function fieldLabel(t: ReturnType<typeof useTranslation>['t'], field: string): string {
  const suffix = field.endsWith('Ar') ? ` (${t('bilingual.arabic')})` : field.endsWith('En') ? ` (${t('bilingual.english')})` : '';
  const base = field.replace(/(Ar|En)$/, '');

  const named: Record<string, string> = {
    name: t('items.name'),
    title: t('offers.offerTitle'),
    question: t('faqs.question'),
    answer: t('faqs.answer'),
    description: t('items.description'),
    address: t('branches.address'),
    body: t('about.body'),
    basePrice: t('items.basePrice'),
    price: t('addons.optionPrice'),
    imageId: t('media.selectImage'),
    image: t('media.selectImage'),
    timezone: t('branches.timezone'),
    latitude: t('branches.latitude'),
    longitude: t('branches.longitude'),
    // The API emits `categoryId`. The old entry here was `categoryPublished`,
    // a name nothing ever sends, so this fell through to the raw identifier.
    categoryId: t('categories.title'),
    items: t('offers.items'),
    startsAt: t('offers.startsAt'),
    endsAt: t('offers.endsAt'),
  };

  return (named[base] ?? t('publish.blockedField')) + suffix;
}

// ----------------------------------------------------------------- confirm

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  tone = 'danger',
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: ReactNode;
  body: ReactNode;
  confirmLabel: ReactNode;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      }
    >
      {body}
    </Modal>
  );
}

// ---------------------------------------------------------------- lifecycle

export interface LifecycleHandlers {
  onPublish?: () => void;
  onUnpublish?: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
}

/**
 * The same four actions, folded into a row's overflow menu.
 *
 * `LifecycleActions` below renders them as a row of buttons, which is right on
 * a detail page where there is one record and room to show its state. On a list
 * it was wrong: four buttons on every row, in three different treatments,
 * wrapped two by two, repeated forty-four times.
 *
 * Nothing is dropped in the fold — each button becomes a menu item with the
 * same label, the same icon and the same handler, including the confirmation
 * dialogs. `extra` is for the actions a particular list adds on top, such as
 * marking a dish unavailable.
 */
export function RowActions({
  status,
  translation,
  handlers,
  busy,
  canEdit,
  archiveWarning,
  extra = [],
  label,
}: {
  status: PublicationStatus;
  translation?: AdminTranslationStatus;
  handlers: LifecycleHandlers;
  busy?: boolean;
  canEdit: boolean;
  archiveWarning?: ReactNode;
  /** Actions this list adds above the lifecycle ones. */
  extra?: ActionMenuItem[];
  /** Names the menu for a screen reader, e.g. the record it belongs to. */
  label?: string;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState<'archive' | 'unpublish' | null>(null);

  if (!canEdit) return null;

  const publishable = translation?.publishable ?? true;
  const items: ActionMenuItem[] = [...extra];

  if (status !== 'PUBLISHED' && status !== 'ARCHIVED' && handlers.onPublish) {
    items.push({
      label: t('publish.publish'),
      icon: <Send className="dio-icon" aria-hidden="true" />,
      // A record missing a translation cannot go live, and the menu says so
      // rather than failing on the click.
      disabled: !publishable,
      title: publishable ? undefined : t('publish.blockedTitle'),
      onSelect: () => handlers.onPublish?.(),
    });
  }
  if (status === 'PUBLISHED' && handlers.onUnpublish) {
    items.push({
      label: t('publish.unpublish'),
      icon: <EyeOff className="dio-icon" aria-hidden="true" />,
      onSelect: () => setConfirming('unpublish'),
    });
  }
  if (status === 'ARCHIVED' && handlers.onRestore) {
    items.push({
      label: t('publish.restore'),
      icon: <ArchiveRestore className="dio-icon" aria-hidden="true" />,
      onSelect: () => handlers.onRestore?.(),
    });
  }
  if (status !== 'ARCHIVED' && handlers.onArchive) {
    items.push({
      label: t('publish.archive'),
      icon: <Archive className="dio-icon" aria-hidden="true" />,
      danger: true,
      onSelect: () => setConfirming('archive'),
    });
  }

  return (
    <>
      <ActionMenu items={items} label={label} />

      <ConfirmDialog
        open={confirming === 'unpublish'}
        title={t('publish.confirmUnpublishTitle')}
        body={t('publish.confirmUnpublishBody')}
        confirmLabel={t('publish.unpublish')}
        busy={busy}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          handlers.onUnpublish?.();
        }}
      />

      <ConfirmDialog
        open={confirming === 'archive'}
        title={t('publish.confirmArchiveTitle')}
        body={
          <div className="flex flex-col gap-3">
            <p>{t('publish.confirmArchiveBody')}</p>
            {archiveWarning && <Alert tone="warning">{archiveWarning}</Alert>}
            <p className="dio-hint">{t('publish.restoreNote')}</p>
          </div>
        }
        confirmLabel={t('publish.archive')}
        busy={busy}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          handlers.onArchive?.();
        }}
      />
    </>
  );
}

/**
 * Publish, unpublish, archive, restore — and nothing else. There is no delete
 * anywhere in this API, so there is no delete button to reach for by mistake.
 *
 * Unpublish is reversible and unguarded. Archive hides a record everywhere, so
 * it asks first.
 *
 * This is the detail-page shape: one record, all four actions visible. On a
 * list, use `RowActions` above.
 */
export function LifecycleActions({
  status,
  translation,
  handlers,
  busy,
  canEdit,
  archiveWarning,
}: {
  status: PublicationStatus;
  translation?: AdminTranslationStatus;
  handlers: LifecycleHandlers;
  busy?: boolean;
  canEdit: boolean;
  /** Extra sentence shown in the archive confirmation, e.g. a dependent count. */
  archiveWarning?: ReactNode;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState<'archive' | 'unpublish' | null>(null);

  if (!canEdit) return null;

  const publishable = translation?.publishable ?? true;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {status !== 'PUBLISHED' && status !== 'ARCHIVED' && handlers.onPublish && (
          <Button
            variant="primary"
            size="sm"
            loading={busy}
            disabled={!publishable}
            onClick={handlers.onPublish}
            title={publishable ? undefined : t('publish.blockedTitle')}
          >
            <Send className="dio-icon" aria-hidden="true" />
            {t('publish.publish')}
          </Button>
        )}

        {status === 'PUBLISHED' && handlers.onUnpublish && (
          <Button variant="secondary" size="sm" loading={busy} onClick={() => setConfirming('unpublish')}>
            <EyeOff className="dio-icon" aria-hidden="true" />
            {t('publish.unpublish')}
          </Button>
        )}

        {status !== 'ARCHIVED' && handlers.onArchive && (
          <Button variant="ghost" size="sm" loading={busy} onClick={() => setConfirming('archive')}>
            <Archive className="dio-icon" aria-hidden="true" />
            {t('publish.archive')}
          </Button>
        )}

        {status === 'ARCHIVED' && handlers.onRestore && (
          <Button variant="secondary" size="sm" loading={busy} onClick={handlers.onRestore}>
            <ArchiveRestore className="dio-icon" aria-hidden="true" />
            {t('publish.restore')}
          </Button>
        )}
      </div>

      <ConfirmDialog
        open={confirming === 'unpublish'}
        title={t('publish.confirmUnpublishTitle')}
        body={t('publish.confirmUnpublishBody')}
        confirmLabel={t('publish.unpublish')}
        busy={busy}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          handlers.onUnpublish?.();
        }}
      />

      <ConfirmDialog
        open={confirming === 'archive'}
        title={t('publish.confirmArchiveTitle')}
        body={
          <div className="flex flex-col gap-3">
            <p>{t('publish.confirmArchiveBody')}</p>
            {archiveWarning && <Alert tone="warning">{archiveWarning}</Alert>}
            <p className="dio-hint">{t('publish.restoreNote')}</p>
          </div>
        }
        confirmLabel={t('publish.archive')}
        busy={busy}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          setConfirming(null);
          handlers.onArchive?.();
        }}
      />
    </>
  );
}
